import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import { tr } from "date-fns/locale"; // Türkçe dil desteğini içe aktarın

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

export const lineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        color: "#e0e0e0",
        usePointStyle: true,
        pointStyle: "circle",
      },
      onClick: function (e, legendItem, legend) {
        const index = legendItem.datasetIndex;
        const ci = legend.chart;
        const meta = ci.getDatasetMeta(index);

        // Veri setinin görünürlüğünü değiştir
        meta.hidden =
          meta.hidden === null ? !ci.data.datasets[index].hidden : null;

        // Hacim seçildiğinde y1 ekseninin görünürlüğünü değiştir
        if (ci.data.datasets[index].yAxisID === "y1") {
          const y1Scale = ci.scales.y1;
          y1Scale.options.display = !meta.hidden;
        }

        // Grafik güncelle
        ci.update();
      },
    },
  },
  scales: {
    y: {
      position: "right",
      ticks: {
        color: "#e0e0e0",
      },
      grid: {
        color: "#444",
      },
      title: {
        display: true,
        text: "Fiyat (TL)",
        color: "#e0e0e0",
      },
    },
    y1: {
      position: "left",
      ticks: {
        color: "#e0e0e0",
        // Hacim değerlerini milyon TL formatında göster
        callback: function (value) {
          return value / 1000000;
        },
      },
      grid: {
        display: false,
      },
      title: {
        display: true,
        text: "Hacim (Milyon TL)",
        color: "#e0e0e0",
      },
    },
    x: {
      type: "time",
      time: {
        unit: "day",
        displayFormats: {
          day: "dd MMM yy",
        },
        tooltipFormat: "dd MMM yyyy",
      },
      adapters: {
        date: {
          locale: tr, // Tarih adaptörüne Türkçe dil desteği ekleyin
        },
      },
      ticks: {
        color: "#e0e0e0",
        autoSkip: true,
        maxTicksLimit: 12,
      },
      grid: {
        color: "#444",
      },
    },
  },
};

export const generateChartTarihselFiyat = (
  tarihselFiyatBilgileri,
  isMobile
) => {
  // Veriler null veya undefined ise null döndür
  if (!tarihselFiyatBilgileri || tarihselFiyatBilgileri.length === 0) {
    return null;
  }

  // Verilerin geçerli olduğundan emin ol
  const validData =
    Array.isArray(tarihselFiyatBilgileri) &&
    tarihselFiyatBilgileri.length > 0 &&
    tarihselFiyatBilgileri[0].Date &&
    tarihselFiyatBilgileri[0].Close !== undefined;

  if (!validData) {
    return null;
  }

  let fiyatlar = tarihselFiyatBilgileri.map((item) => ({
    tarih: item.Date,
    fiyat: item.Close,
    hacim: item.Volume, // Hacim verilerini ekle
  }));

  // Orjinal fiyat verilerinden 100 günlük hareketli ortalamayı hesapla
  const allPrices = fiyatlar.map((item) => item.fiyat);
  const movingAvg100 = calculateMovingAverage(allPrices, 100);

  // Grafik dataseti oluştur - sıralamayı hacim, ortalama, fiyat şeklinde ayarla
  const datasets = [
    {
      label: "Hacim",
      data: fiyatlar.map((item) => item.hacim),
      backgroundColor: "#3B82F635", // Hacim çubuk rengi
      borderColor: "#3B82F635",
      borderWidth: 1,
      type: "bar",
      yAxisID: "y1",
      order: 3,
    },
    {
      label: "100 Günlük Hareketli Ortalama",
      data: movingAvg100,
      borderColor: "#9966FFB3", // Güncellenen hareketli ortalama rengi
      borderWidth: 1,
      pointRadius: 0, // Nokta gösterme
      fill: false,
      borderDash: [5, 5], // Kesikli çizgi
      type: "line",
      yAxisID: "y",
      order: 2,
    },
    {
      label: "Kapanış Fiyatı (TL)",
      data: fiyatlar.map((item) => item.fiyat),
      borderColor: "#F59F0BB3", // Güncellenen fiyat çizgi rengi
      backgroundColor: "rgba(245, 159, 11, 0.2)", // Dolgu rengi
      borderWidth: 2,
      pointRadius: fiyatlar.length > 50 ? 0 : 4, // Nokta boyutu
      pointBackgroundColor: "#F59F0BB3", // Nokta rengi
      type: "line",
      yAxisID: "y",
      order: 1,
    },
  ];

  // Mobil ekranda ve 50'den fazla veri noktası varsa örnekleme yap
  if (isMobile && fiyatlar.length > 50) {
    // Hareketli ortalama verilerini örneklemeden önce fiyatlarla eşleştir
    const fullDataWithMA = fiyatlar.map((item, index) => ({
      ...item,
      ma100: movingAvg100[index],
    }));

    // Veriyi örnekle
    const sampledData = sampleData(fullDataWithMA, 50);

    // Örneklenmiş veriyi ayır
    fiyatlar = sampledData.map((item) => ({
      tarih: item.tarih,
      fiyat: item.fiyat,
      hacim: item.hacim,
    }));

    // Örneklenmiş hareketli ortalama
    const sampledMA100 = sampledData.map((item) => item.ma100);

    // Grafik dataseti güncelle - yeni sıralamaya göre
    datasets[0].data = fiyatlar.map((item) => item.hacim); // Hacim
    datasets[1].data = sampledMA100; // Hareketli Ortalama
    datasets[2].data = fiyatlar.map((item) => item.fiyat); // Fiyat
  }
  // Geniş ekranda ve 250'den fazla veri noktası varsa örnekleme yap
  else if (!isMobile && fiyatlar.length > 250) {
    // Hareketli ortalama verilerini örneklemeden önce fiyatlarla eşleştir
    const fullDataWithMA = fiyatlar.map((item, index) => ({
      ...item,
      ma100: movingAvg100[index],
    }));

    // Veriyi örnekle
    const sampledData = sampleData(fullDataWithMA, 250);

    // Örneklenmiş veriyi ayır
    fiyatlar = sampledData.map((item) => ({
      tarih: item.tarih,
      fiyat: item.fiyat,
      hacim: item.hacim,
    }));

    // Örneklenmiş hareketli ortalama
    const sampledMA100 = sampledData.map((item) => item.ma100);

    // Grafik dataseti güncelle
    datasets[0].data = fiyatlar.map((item) => item.hacim); // Hacim
    datasets[1].data = sampledMA100; // Hareketli Ortalama
    datasets[2].data = fiyatlar.map((item) => item.fiyat); // Fiyat
  }

  // Grafik verilerini döndür
  return {
    labels: fiyatlar.map((item) => item.tarih),
    datasets: datasets,
  };
};

// Örnekleme fonksiyonu - büyük veri setlerini daha küçük bir örneğe indirger
const sampleData = (data, targetSampleSize) => {
  const dataLength = data.length;

  // Eğer veri boyutu zaten hedeflenen örneklem boyutundan küçük veya eşitse, tüm veriyi döndürün
  if (dataLength <= targetSampleSize) {
    return data;
  }

  // Örnekleme için adım boyutunu hesapla
  const step = Math.floor(dataLength / targetSampleSize);

  // Örneklenmiş veriyi oluştur
  const sampledData = [];

  // Her zaman ilk ve son veriyi dahil et
  sampledData.push(data[0]);

  // Adım boyutuna göre orta noktaları seç
  for (let i = step; i < dataLength - step; i += step) {
    sampledData.push(data[i]);
  }

  // Son veriyi ekle (eğer zaten eklenmemişse)
  if (sampledData[sampledData.length - 1] !== data[dataLength - 1]) {
    sampledData.push(data[dataLength - 1]);
  }

  return sampledData;
};

// 100 günlük hareketli ortalama hesaplama fonksiyonu
const calculateMovingAverage = (data, period) => {
  const result = [];

  // İlk period-1 gün için hareketli ortalama hesaplanamaz (yeterli veri yok)
  for (let i = 0; i < period - 1; i++) {
    result.push(null);
  }

  // period. günden itibaren hareketli ortalama hesapla
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const sum = slice.reduce((acc, val) => acc + val, 0);
    const avg = sum / period;
    result.push(avg);
  }

  return result;
};
